import React, {useState, useEffect} from 'react';
import apiInstance from '../../utils/axios';
import UserData from '../plugin/UserData';
import { Link } from 'react-router-dom';
import Slidebar from './Slidebar';
import Swal from 'sweetalert2';
import PriceFormat_Basic from '../../components/commerce-ui/price-format-basic';
import { 
    Heart, 
    HeartOff, 
    ShoppingBag, 
    Tag, 
    ExternalLink, 
    Sparkles,
    LogIn,
    Trash2,
    Eye
} from 'lucide-react';

function Wishlist() {
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removingId, setRemovingId] = useState(null);

    const userData = UserData();

    const fetchWishlist = async () => {
        if (!userData?.user_id) return;
        
        try {
            setLoading(true);
            const response = await apiInstance.get(`customer/wishlist/${userData.user_id}/`);
            setWishlist(response.data);
        } catch (error) {
            console.error('Error fetching wishlist:', error);
            setWishlist([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWishlist();
    }, [userData?.user_id]);

    const toggleWishlist = async (productId) => {
        if (!userData?.user_id) {
            Swal.fire({
                title: "Por favor, inicia sesión para gestionar tu lista de deseos",
                icon: 'warning',
                confirmButtonText: "Aceptar"
            });
            return;
        }

        try {
            setRemovingId(productId);
            const formData = new FormData();
            formData.append("product_id", productId);
            formData.append("user_id", userData.user_id);

            const response = await apiInstance.post(`customer/wishlist/${userData.user_id}/`, formData);
            
            await fetchWishlist();

            Swal.fire({
                title: response.data.message,
                icon: 'success',
                confirmButtonText: "Aceptar",
                toast: true,
                position: 'top-end',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error toggling wishlist:', error);
            Swal.fire({
                title: "Error al actualizar la lista de deseos",
                icon: 'error',
                confirmButtonText: "Aceptar"
            });
        } finally {
            setRemovingId(null);
        }
    };

    if (!userData?.user_id) {
        return (
            <div className="wishlist-page">
                <div className="login-required">
                    <div className="login-card">
                        <div className="login-icon">
                            <Heart size={48} />
                        </div>
                        <h2>Inicio de Sesión Requerido</h2>
                        <p>Por favor, inicia sesión para ver tu lista de deseos</p>
                        <Link to="/login/" className="login-btn">
                            <LogIn size={20} />
                            Iniciar Sesión
                        </Link>
                    </div>
                </div>
                <style>{wishlistStyles}</style>
            </div>
        );
    }

    return (
        <div className="wishlist-page">
            <div className="wishlist-container">
                <div className="wishlist-layout">
                    <aside className="wishlist-sidebar">
                        <Slidebar />
                    </aside>

                    <main className="wishlist-main">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner" />
                                <p>Cargando tu lista de deseos...</p>
                            </div>
                        ) : wishlist && wishlist.length > 0 ? (
                            <div className="products-grid">
                                {wishlist.map((w, index) => {
                                    const product = w.product;
                                    const hasDiscount = product.old_price && product.old_price > product.price;
                                    const discountPercent = hasDiscount 
                                        ? Math.round((1 - product.price / product.old_price) * 100) 
                                        : 0;

                                    return (
                                        <div 
                                            key={w.id || index} 
                                            className={`product-card ${removingId === product.id ? 'removing' : ''}`}
                                        >
                                            {/* Image Container */}
                                            <div className="card-image-wrapper">
                                                <Link to={`/detail/${product.slug}/`}>
                                                    <img
                                                        src={product.image}
                                                        alt={product.title_translated || product.title}
                                                        className="card-image"
                                                    />
                                                </Link>

                                                {/* Discount Badge */}
                                                {hasDiscount && (
                                                    <div className="discount-badge">
                                                        <Sparkles size={12} />
                                                        -{discountPercent}%
                                                    </div>
                                                )}

                                                {/* Wishlist Remove Button */}
                                                <button
                                                    onClick={() => toggleWishlist(product.id)}
                                                    className="wishlist-remove-btn"
                                                    title="Remover de la lista de deseos"
                                                    disabled={removingId === product.id}
                                                >
                                                    {removingId === product.id ? (
                                                        <div className="mini-spinner" />
                                                    ) : (
                                                        <HeartOff size={18} />
                                                    )}
                                                </button>

                                                {/* Quick View Overlay */}
                                                <Link to={`/detail/${product.slug}/`} className="quick-view-overlay">
                                                    <Eye size={24} />
                                                    <span>Ver Producto</span>
                                                </Link>
                                            </div>

                                            {/* Card Content */}
                                            <div className="card-content">
                                                {/* Category */}
                                                {product.category && (
                                                    <Link 
                                                        to={`/products?category=${product.category.id}`} 
                                                        className="category-tag"
                                                    >
                                                        <Tag size={12} />
                                                        {product.category.title_translated || product.category.title}
                                                    </Link>
                                                )}

                                                {/* Title */}
                                                <Link to={`/detail/${product.slug}/`} className="product-title-link">
                                                    <h3 className="product-title">
                                                        {product.title_translated || product.title}
                                                    </h3>
                                                </Link>

                                                {/* Price */}
                                                <div className="price-section">
                                                    <span className="current-price">
                                                        <PriceFormat_Basic value={product.price} />
                                                    </span>
                                                    {hasDiscount && (
                                                        <span className="old-price">
                                                            <PriceFormat_Basic value={product.old_price} />
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Actions */}
                                                <div className="card-actions">
                                                    <Link to={`/detail/${product.slug}/`} className="view-btn">
                                                        <ExternalLink size={16} />
                                                        Ver Detalles
                                                    </Link>
                                                    <button
                                                        onClick={() => toggleWishlist(product.id)}
                                                        className="remove-btn"
                                                        disabled={removingId === product.id}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <Heart size={48} />
                                </div>
                                <h3>Tu lista de deseos está vacía</h3>
                                <p>Comienza a agregar productos que te gusten</p>
                                <Link to="/products/" className="explore-btn">
                                    <ShoppingBag size={20} />
                                    Explorar Productos
                                </Link>
                            </div>
                        )}
                    </main>
                </div>
            </div>

            <style>{wishlistStyles}</style>
        </div>
    );
}

const wishlistStyles = `
    .wishlist-page {
        background: #ffffff;
        min-height: 100vh;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }

    /* Layout */
    .wishlist-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 32px 24px 60px;
    }

    .wishlist-layout {
        display: flex;
        gap: 32px;
    }

    .wishlist-sidebar {
        width: 320px;
        flex-shrink: 0;
    }

    .wishlist-main {
        flex: 1;
        min-width: 0;
    }

    /* Loading */
    .loading-state {
        text-align: center;
        padding: 80px 20px;
    }

    .spinner {
        width: 48px;
        height: 48px;
        border: 4px solid #e2e8f0;
        border-top-color: #ef4444;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    /* Products Grid */
    .products-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 24px;
    }

    /* Product Card */
    .product-card {
        background: white;
        border-radius: 24px;
        overflow: hidden;
        border: 1px solid #e2e8f0;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        transition: all 0.3s ease;
    }

    .product-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }

    .product-card.removing {
        opacity: 0.5;
        transform: scale(0.95);
    }

    .card-image-wrapper {
        position: relative;
        aspect-ratio: 1;
        overflow: hidden;
        background: #f8fafc;
    }

    .card-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.4s ease;
    }

    .product-card:hover .card-image {
        transform: scale(1.05);
    }

    .discount-badge {
        position: absolute;
        top: 16px;
        left: 16px;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 6px 12px;
        background: linear-gradient(135deg, #ef4444 0%, #f97316 100%);
        color: white;
        font-size: 12px;
        font-weight: 700;
        border-radius: 20px;
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    }

    .wishlist-remove-btn {
        position: absolute;
        top: 16px;
        right: 16px;
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: white;
        border: none;
        color: #ef4444;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .wishlist-remove-btn:hover {
        background: #ef4444;
        color: white;
        transform: scale(1.1);
    }

    .mini-spinner {
        width: 18px;
        height: 18px;
        border: 2px solid #fecaca;
        border-top-color: #ef4444;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }

    .quick-view-overlay {
        position: absolute;
        inset: 0;
        background: rgba(15, 23, 42, 0.8);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: white;
        opacity: 0;
        transition: opacity 0.3s ease;
        text-decoration: none;
        font-weight: 600;
        font-size: 14px;
    }

    .product-card:hover .quick-view-overlay {
        opacity: 1;
    }

    .card-content {
        padding: 20px;
    }

    .category-tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        background: #f1f5f9;
        color: #64748b;
        font-size: 11px;
        font-weight: 600;
        border-radius: 6px;
        text-decoration: none;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 12px;
        transition: all 0.2s ease;
    }

    .category-tag:hover {
        background: #e2e8f0;
        color: #0f172a;
    }

    .product-title-link {
        text-decoration: none;
    }

    .product-title {
        font-size: 16px;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 12px;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        transition: color 0.2s ease;
    }

    .product-title-link:hover .product-title {
        color: #3b82f6;
    }

    .price-section {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 16px;
    }

    .current-price {
        font-size: 20px;
        font-weight: 800;
        color: #0f172a;
    }

    .old-price {
        font-size: 14px;
        color: #94a3b8;
        text-decoration: line-through;
    }

    .card-actions {
        display: flex;
        gap: 10px;
    }

    .view-btn {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px 20px;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        color: white;
        border: none;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 600;
        text-decoration: none;
        transition: all 0.2s ease;
    }

    .view-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
        color: white;
    }

    .remove-btn {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fee2e2;
        color: #ef4444;
        border: none;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .remove-btn:hover {
        background: #ef4444;
        color: white;
    }

    /* Empty State */
    .empty-state {
        text-align: center;
        padding: 80px 20px;
        background: white;
        border-radius: 24px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
    }

    .empty-icon {
        width: 96px;
        height: 96px;
        background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
        border-radius: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 24px;
        color: #ef4444;
    }

    .empty-state h3 {
        font-size: 22px;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 8px;
    }

    .empty-state p {
        font-size: 15px;
        color: #64748b;
        margin: 0 0 24px;
    }

    .explore-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 14px 28px;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        color: white;
        border: none;
        border-radius: 14px;
        font-size: 15px;
        font-weight: 600;
        text-decoration: none;
        transition: all 0.2s ease;
    }

    .explore-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
        color: white;
    }

    /* Login Required */
    .login-required {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    }

    .login-card {
        text-align: center;
        padding: 60px 40px;
        background: white;
        border-radius: 32px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
        max-width: 400px;
    }

    .login-icon {
        width: 80px;
        height: 80px;
        background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
        border-radius: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 24px;
        color: #ef4444;
    }

    .login-card h2 {
        font-size: 24px;
        font-weight: 800;
        color: #0f172a;
        margin: 0 0 8px;
    }

    .login-card p {
        font-size: 15px;
        color: #64748b;
        margin: 0 0 24px;
    }

    .login-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 14px 28px;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        color: white;
        border: none;
        border-radius: 14px;
        font-size: 15px;
        font-weight: 600;
        text-decoration: none;
        transition: all 0.2s ease;
    }

    .login-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
        color: white;
    }

    /* Responsive */
    @media (max-width: 1024px) {
        .wishlist-sidebar {
            display: none;
        }

        .products-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 768px) {
        .wishlist-container {
            padding: 20px 16px 40px;
        }

        .products-grid {
            grid-template-columns: 1fr;
        }
    }
`;

export default Wishlist;